// The server's checklist, hydrated with this client's copy (ADR-0027).
//
// What counts as done, and which step comes next, are decided by
// `workspace_progress()` in SQL — once, for both clients, next to the tables the
// answer depends on. The same four rules used to be written here in TypeScript
// and again in Dart, along with the table and column names behind them.
//
// What stays here is the half that genuinely differs between a web page and a
// handset: the titles, the copy, the icons and the route each step leads to.

import { supabase } from "../lib/supabase";
import { DASHBOARD_ROUTE } from "../dashboard";
import { MILESTONES, type Milestone, type MilestoneId } from "./milestones";
import type { EngagementFacts } from "./facts";

/** One step, as the server sends it. Ids match `MILESTONES`. */
export interface ServerStep {
  id: MilestoneId;
  done: boolean;
  /** False when this step is somebody else's job in this workspace. */
  allowed: boolean;
}

/** The `progress` object of a session or a `workspace_progress()` call. */
export interface ServerProgress {
  facts: EngagementFacts;
  steps: ServerStep[];
  done_count: number;
  complete: boolean;
  fresh: boolean;
  next: MilestoneId | null;
}

export interface MilestoneState {
  milestone: Milestone;
  /** The workspace has done this. */
  done: boolean;
  /** This user is permitted to do it. When false it is somebody else's job. */
  allowed: boolean;
}

export interface Progress {
  steps: MilestoneState[];
  /** Steps completed, out of MILESTONES.length. Counts the workspace, not the user. */
  doneCount: number;
  complete: boolean;
  /** Nothing at all has been done yet — the true first-run case. */
  fresh: boolean;
  /**
   * The first step that is neither done nor somebody else's job. This is what
   * `/start` navigates to and what the strip points at. Null when there is
   * nothing left this person can usefully be sent to.
   */
  next: MilestoneState | null;
}

/**
 * Server answer in, renderable checklist out. Pure.
 *
 * `MILESTONES` drives the order and the copy, so a step the server does not
 * mention renders as not-done rather than disappearing — an older client
 * meeting a newer database must not lose a row out of the middle of a list.
 */
export function progressFrom(server: ServerProgress): Progress {
  const byId = new Map(server.steps?.map((s) => [s.id, s]) ?? []);

  const steps: MilestoneState[] = MILESTONES.map((milestone) => {
    const sent = byId.get(milestone.id);
    return {
      milestone,
      done: sent?.done ?? false,
      allowed: sent?.allowed ?? false,
    };
  });

  return {
    steps,
    doneCount: server.done_count,
    complete: server.complete,
    fresh: server.fresh,
    next: steps.find((s) => s.milestone.id === server.next) ?? null,
  };
}

/**
 * Re-read the checklist for one workspace, without the gate.
 *
 * Connecting an account, publishing and uploading all happen on other screens.
 * This is the cheap half of `my_session()` — deliberately a separate function,
 * because re-running admission on every navigation would be absurd.
 */
export async function loadProgress(workspaceId: string): Promise<ServerProgress | null> {
  const { data, error } = await supabase.rpc("workspace_progress", {
    p_workspace_id: workspaceId,
  });
  if (error) throw new Error(error.message);
  return (data ?? null) as ServerProgress | null;
}

/**
 * Where to send someone who has just signed in.
 *
 * The dashboard is the answer whenever there is no better one — an established
 * workspace, a member with nothing outstanding they may act on, or a workspace
 * whose state could not be read.
 *
 * It used to be `/compose`, on the grounds that composing is what the product
 * is for. That was true and still landed people badly: a returning publisher
 * arrived at an empty text box with no sign of what was already scheduled, what
 * had failed overnight, or which account had stopped working. The dashboard
 * answers those first and carries the composer on it, so nothing was lost by
 * moving — the box is still the first thing on the page.
 *
 * A workspace with steps outstanding still goes to its next step instead; this
 * is only the fallback.
 */
export const FALLBACK_ROUTE = DASHBOARD_ROUTE;

export const landingRoute = (progress: Progress | null): string =>
  progress?.next?.milestone.route ?? FALLBACK_ROUTE;
